import axios from "axios";
import moment from "moment/moment";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import toast from "react-hot-toast";

const UnavailableSlotPopup = ({ isOpen, onClose }) => {
  const [experienceId, setExperienceId] = useState("");
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem("authToken");
  //   const host = JSON.parse(localStorage.getItem("user"));
  //   const hostId = host?.id;
  const [host, sethost] = useState();

  const [range, setRange] = useState([null, null]);
  const [startDate, endDate] = range;

  const [openCalendar, setOpenCalendar] = useState(false);
  const [activeDate, setActiveDate] = useState(null);

  // Slots coming from API
  const [slots, setSlots] = useState([]);
  const [minDate, setMinDate] = useState(null);
  const [maxDate, setMaxDate] = useState(null);

  // Selected unavailable slots grouped by date
  const [dateSlots, setDateSlots] = useState({});

  // -----------------------------------------------------
  // EXPERIENCE LIST API
  // -----------------------------------------------------
  const fetchListings = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/host/experience-list`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data?.data) setListings(res.data.data);
      if (res.data?.host) sethost(res.data.host?.id);
    } catch (err) {
      console.error("Failed to fetch experience list", err);
    } finally {
      setLoading(false);
    }
  };

  // Load Experiences when popup opens
  useEffect(() => {
    if (isOpen) {
      fetchListings();
      setSlots([]);
    }
  }, [isOpen]);

  // -----------------------------------------------------
  // GET SLOTS API (Without package_id)
  // -----------------------------------------------------
  const fetchSlots = async () => {
    try {
      if (!experienceId || !activeDate) return;
      setLoading(true);

      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/getSlotsBySingleDate`,
        {
          params: {
            experience_id: experienceId,
            host_id: host,
            date: activeDate,
          },
        }
      );

      if (res.data?.status) {
  const formatted = (res.data.data.slots || []).map(s => ({
    ...s,
    from_time: s.from_time.slice(0, 5), 
    to_time: s.to_time.slice(0, 5),
    is_unavailable: s.is_unavailable // keep it!
  }));

  setSlots(formatted);

  // Auto-mark unavailable slots in dateSlots
  const unavailable = formatted.filter(s => s.is_unavailable);

  setDateSlots(prev => ({
    ...prev,
    [activeDate]: unavailable.length ? unavailable : (prev[activeDate] || [])
  }));
}

    } catch (err) {
      console.error("Error fetching slots:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnavailableSlots = async () => {
    try {
      const expId = experienceId;
      if (!expId) return;

      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/host/get-unavailable-slots?experience_id=${expId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data?.status) {
        // CASE 1: No unavailable slots → user will manually select
        if (!res.data.min_date && !res.data.max_date) {
          setMinDate(null);
          setMaxDate(null);
          setRange([null, null]);
          setActiveDate(null);
          setDateSlots({});
          return;
        }

        // CASE 2: Unavailable slots exist → apply min/max
        setMinDate(new Date(res.data.min_date));
        setMaxDate(new Date(res.data.max_date));

        const formatted = res.data.data.map((slot) => ({
          date: slot.date,
          from_time: slot.from_time.slice(0, 5),
          to_time: slot.to_time.slice(0, 5),
        }));

        const grouped = formatted.reduce((acc, item) => {
          if (!acc[item.date]) acc[item.date] = [];
          acc[item.date].push(item);
          return acc;
        }, {});

        setDateSlots(grouped);

        // Auto-set calendar range
        setRange([new Date(res.data.min_date), new Date(res.data.max_date)]);
        setActiveDate(res.data.min_date);
      }
    } catch (error) {
      console.error("Error fetching unavailable slots:", error);
    }
  };

  // Fetch slots when experience OR selected date changes
  useEffect(() => {
    if (experienceId && activeDate) {
      fetchSlots();
    }
  }, [experienceId, activeDate]);

  const saveUnavailableSlots = async () => {
    if (!experienceId) return;

    // Flatten the dateSlots object into an array suitable for the API
    const unavailableSlots = Object.entries(dateSlots).flatMap(
      ([date, slots]) =>
        slots.map((slot) => ({
          date,
          // Convert from HH:MM:SS to HH:MM
          from_time: slot.from_time.slice(0, 5),
          to_time: slot.to_time.slice(0, 5),
        }))
    );

    if (unavailableSlots.length === 0) {
      toast.error("Please select at least one slot to mark as unavailable");
      return;
    }

    const payload = {
      experience_id: experienceId,
      slots: unavailableSlots,
    };

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE}/host/save-unavailable-slots`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.status) {
        toast.success("Unavailable slots marked successfully 🚫");
        setDateSlots({});
        setSlots([]);
      } else {
        toast.error(res.data.message || "Failed to save slots");
      }
    } catch (err) {
      console.error("Error saving unavailable slots:", err);
      toast.error("Something went wrong while saving slots");
    }
  };

  useEffect(() => {
    if (experienceId) {
      fetchUnavailableSlots(); // Load already marked unavailable slot
    }
  }, [experienceId]);

  // -----------------------------------------------------
  // HANDLE DATE CLICK
  // -----------------------------------------------------
  const handleCalendarClick = (date) => {
    const d = moment(date).format("YYYY-MM-DD");
    setActiveDate(d);
  };

  // -----------------------------------------------------
  // SLOT SELECT
  // -----------------------------------------------------
const handleSlotSelect = (slot) => {
  if (!activeDate) return;

  const isSelected = slot.is_unavailable;

  // Toggle the slot
  setSlots(prev =>
    prev.map(s =>
      s.id === slot.id
        ? { ...s, is_unavailable: !isSelected }
        : s
    )
  );

  // Update dateSlots
  setDateSlots(prev => {
    const existing = prev[activeDate] || [];

    // If already selected → unselect it
    if (isSelected) {
      return {
        ...prev,
        [activeDate]: existing.filter(s => s.id !== slot.id)
      };
    }

    // If newly selected → add it
    return {
      ...prev,
      [activeDate]: [...existing, { ...slot, is_unavailable: true }]
    };
  });
};




  if (!isOpen) return null;

  return (
    <div className="us_overlay unavali_slt_mld">
      <div className="us_popup">
        <div className="us_header">
          <h4>Mark Unavailable Slot</h4>
          <button className="us_close_btn" onClick={onClose}>
            ✖
          </button>
        </div>

        {/* EXPERIENCE DROPDOWN */}
        <div className="row mb-3">
          <div className="col-lg-6">
            <div className="exrtss_rangess">
            <label>Select Experience</label>
            <select
              className="form-control"
              value={experienceId}
              onChange={(e) =>
                setExperienceId(e.target.value ? Number(e.target.value) : "")
              }
            >
              <option value="">-- Select Experience --</option>
              {loading && <option>Loading...</option>}

              {!loading &&
                listings?.map((item) => (
                  <option key={item.id} value={item.experience_id}>
                    {item.title} {/* Name shown in dropdown */}
                  </option>
                ))}
            </select>
          </div>
          </div>

          {/* DATE RANGE */}
          <div className="col-lg-6">
            <div className="dat_rangess">
            <label>Select Date Range</label>

            <input
              type="text"
              readOnly
              className="form-control"
              value={
                startDate && endDate
                  ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                  : "Select Date"
              }
              onClick={() => setOpenCalendar(true)}
            />

            {openCalendar && (
              <div className="calendar_popup">
                <DatePicker
                  inline
                  selectsRange
                  startDate={startDate}
                  endDate={endDate}
                   minDate={startDate}   // ← user cannot click before this
  maxDate={endDate} 
                  onChange={(update) => {
                    setRange(update);

                    if (update?.[0]) {
                      const formattedStart = moment(update[0]).format(
                        "YYYY-MM-DD"
                      );
                      setActiveDate(formattedStart);
                    }

                    if (update?.[0] && update?.[1]) setOpenCalendar(false);
                  }}
                />
              </div>
            )}
            </div>
          </div>
        </div>

        {/* NEXT PART AFTER DATE RANGE */}
        {startDate && endDate && (
          <div className="row mt-4">
            {/* SLOTS */}
           <div className="col-lg-4">
            <div className="mng_bbbx">
  <h5>Slots</h5>
  <div className="us_list_container">

    {!activeDate && <p>Select a date from the calendar</p>}

    {activeDate && slots.length === 0 && (
      <p>No slots available for this date</p>
    )}


    {activeDate &&
      slots.map((slot) => {
        const isSelected = slot.is_unavailable || dateSlots[activeDate]?.some(
  (s) => s.from_time === slot.from_time && s.to_time === slot.to_time
);


        return (
          <label
            key={slot.id}
            className={`us_slot_item ${isSelected ? "active" : ""}`}
            style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
          >
            {/* RADIO BUTTON */}
           <input
  type="checkbox"
  checked={isSelected}
  onChange={() => handleSlotSelect(slot)}
/>


            {/* SLOT TEXT */}
            <span>
              {slot.from_time} - {slot.to_time} ({slot.package_name})
            </span>
          </label>
        );
      })}
  </div>
  </div>
</div>


            {/* INLINE CALENDAR */}
            <div className="col-lg-4 text-center">
              <div className="mng_bbbx">
              <h5>Calendar</h5>
              <DatePicker
                inline
                selectsRange={false}
                selected={startDate}
                startDate={startDate}
                endDate={endDate}
                 minDate={startDate}   // ← user cannot click before this
  maxDate={endDate} 
                onSelect={handleCalendarClick}
              />
              </div>
            </div>

            {/* DETAILS */}
            <div className="col-lg-4">
               <div className="mng_bbbx">
              <h5>Details</h5>
              <div className="details_box">
                {Object.keys(dateSlots).length === 0 && <p>No selection yet</p>}

                {Object.keys(dateSlots).map((date) => (
                  <div key={date} className="mb-2">
                    <strong>{date}</strong>
                    <ul>
                      {dateSlots[date].map((slot) => (
                        <li key={slot.id}>
                          {slot.from_time} - {slot.to_time} ({slot.package_name}
                          )
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}

                <div className="mt-3">
                  <strong>Total Unavailable Slots: </strong>
                  {Object.values(dateSlots).reduce(
                    (total, slots) => total + slots.length,
                    0
                  )}
                </div>

                <button
                  className="btn btn-primary mt-3"
                  onClick={saveUnavailableSlots}
                >
                  Save
                </button>
              </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UnavailableSlotPopup;
